/**
 * CashAppQueue — landlord page at /cash-app for the Smart Cash-Application
 * exception queue.
 *
 * Lists incoming Stripe payments that the matcher couldn't 100% auto-apply.
 * Landlord picks the right lease, OR dismisses (one-off transfer / unrelated).
 * The "Run matcher" button re-runs the engine across the last 30 days.
 *
 * KPI strip up top shows the live STP rate.
 */
import { useEffect, useState } from "react";
import { api, formatCents } from "@/lib/api";
import { useRealtime } from "@/lib/useRealtime";
import { PageHeader, Card, Button, Loader, EmptyState } from "@/components/ui/Primitives";
import { CheckCircle2, X, Sparkles, RefreshCw, AlertTriangle, Trophy, Wifi } from "lucide-react";
import { toast } from "sonner";

export default function CashApp() {
  const [stpRate, setStpRate] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [liveBadge, setLiveBadge] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, q] = await Promise.all([
        api.get("/cash-app/stp-rate?days=30").catch(() => ({ data: null })),
        api.get("/cash-app/queue?limit=100").catch(() => ({ data: { items: [] } })),
      ]);
      setStpRate(s.data);
      setQueue(q.data?.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Live updates — when the matcher publishes 'auto_applied' or 'exception_added'
  // the SSE handler bumps a badge and quietly re-fetches without a full page reload.
  useRealtime({
    topics: ["cash-app"],
    onEvent: (e) => {
      if (e.topic !== "cash-app") return;
      setLiveBadge(true);
      setTimeout(() => setLiveBadge(false), 1800);
      load();
    },
  });

  const runMatcher = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/cash-app/run", null, { params: { days: 30 } });
      toast.success(`Matched ${data.auto_applied}/${data.processed} payments · ${data.stp_rate_pct}% STP`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Matcher failed");
    } finally { setRunning(false); }
  };

  const apply = async (row) => {
    const leaseId = row.suggested_lease?.id || row.lease_id;
    if (!leaseId) return toast.error("No suggested lease — open Leases to apply manually.");
    try {
      await api.post(`/cash-app/queue/${row.id}/apply`, { lease_id: leaseId });
      toast.success("Posted to lease");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply");
    }
  };

  const dismiss = async (row) => {
    try {
      await api.post(`/cash-app/queue/${row.id}/dismiss`);
      toast.success("Dismissed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not dismiss");
    }
  };

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            Smart Cash-Application
            {liveBadge && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid="cash-app-live-badge">
                <Wifi className="w-3 h-3 animate-pulse"/> Live
              </span>
            )}
          </span>
        }
        subtitle="Incoming Recorded rent payments are auto-matched to leases. Only the unclear ones land here for your review."
        actions={
          <Button onClick={runMatcher} disabled={running} data-testid="cash-app-run">
            {running ? <RefreshCw className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>} Run matcher
          </Button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card className="p-4" data-testid="kpi-stp-rate">
          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 flex items-center gap-1"><Trophy className="w-3 h-3"/> STP rate</div>
          <div className="font-display text-2xl font-extrabold text-slate-900 mt-1 tabular-nums">{stpRate?.stp_rate_pct ?? 0}<span className="text-base">%</span></div>
          <div className="text-[11px] text-slate-500 mt-0.5">Last 30 days</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Auto-applied</div>
          <div className="font-display text-2xl font-extrabold text-slate-900 mt-1 tabular-nums" data-testid="kpi-auto-applied">{stpRate?.auto_applied ?? 0}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Total payments</div>
          <div className="font-display text-2xl font-extrabold text-slate-900 mt-1 tabular-nums">{stpRate?.total_payments ?? 0}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> Need review</div>
          <div className="font-display text-2xl font-extrabold text-slate-900 mt-1 tabular-nums" data-testid="kpi-queue">{queue.length}</div>
        </Card>
      </div>

      {queue.length === 0 ? (
        <EmptyState
          icon={CheckCircle2}
          title="Exception queue is empty 🎉"
          description="Every recent payment has been auto-posted. Hit ‘Run matcher’ after a fresh batch of Stripe transfers to refresh."
          action={<Button onClick={runMatcher}><Sparkles className="w-4 h-4"/> Run matcher now</Button>}
        />
      ) : (
        <div className="space-y-3">
          {queue.map((row) => (
            <Card key={row.id} className="p-4" data-testid={`queue-row-${row.id}`}>
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-slate-900 tabular-nums">{formatCents(row.amount_cents || 0)}</span>
                    <span className="text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded border bg-slate-50 text-slate-700 border-slate-200">{row.classification || "other"}</span>
                    <span className="text-[10px] text-slate-400">conf {row.confidence ?? 0}</span>
                    <span className="text-[10px] text-slate-400 truncate">{row.reason}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1 truncate">From: <span className="font-mono">{row.payer_email || "—"}</span></div>
                  {row.suggested_lease && (
                    <div className="text-[11px] text-emerald-700 mt-1 truncate">
                      Suggested lease: <strong>{row.suggested_lease.tenant_email}</strong> · {row.suggested_lease.address || "—"} · rent {formatCents(row.suggested_lease.rent_amount_cents || 0)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="ghost" onClick={() => dismiss(row)} className="!px-3 !py-1.5 text-xs text-slate-600" data-testid={`queue-dismiss-${row.id}`}>
                    <X className="w-3.5 h-3.5"/> Dismiss
                  </Button>
                  <Button onClick={() => apply(row)} disabled={!row.suggested_lease} className="!px-3 !py-1.5 text-xs" data-testid={`queue-apply-${row.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5"/> Apply
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
